// components/ProjectForm.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Project, Attachment, ProjectStatus } from '../types';
import { useProjects } from '.././context/ProjectContext.tsx';
import { validateFile, formatFileSize, getFileType } from '../utils/helper.ts';
import { DXFParser } from './visualization/DXFParser.tsx';
import PreviewModal from './PreviewModal.tsx';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api.ts';

interface ProjectFormData {
  name: string;
  client: string;
  location: string;
  description: string;
  dxfContent: string;
  attachments: Attachment[];
}

interface ProjectFormProps {
  currentUser: User;
}

interface FormErrors {
  name?: string;
  client?: string;
  location?: string;
  description?: string;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ currentUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { projects, addProject, fetchProjects } = useProjects();
  const [formData, setFormData] = useState<ProjectFormData>({
    name: "",
    client: "",
    location: "",
    description: "",
    dxfContent: "",
    attachments: []
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);

  const generateProjectId = (): string => {
    const year = new Date().getFullYear();
    const sequence = (projects.length + 1).toString().padStart(3, '0');
    return `PRJ-${year}-${sequence}`;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = "Project name is required";
    if (!formData.client.trim()) newErrors.client = "Client name is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (status: ProjectStatus) => {
    if (!validateForm() || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      // Create a FormData object
      const formDataToSend = new FormData();
      
      // Add project details
      formDataToSend.append('name', formData.name);
      formDataToSend.append('client', formData.client);
      formDataToSend.append('location', formData.location);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('status', status);
      if (formData.dxfContent) {
        formDataToSend.append('dxfContent', formData.dxfContent);
      }
      
      // Convert attachments from the state to actual File objects
      // You'll need to modify your file upload handling to store actual File objects
      // If you're not storing File objects, this part needs to be adjusted
      for (const attachment of formData.attachments) {
        if (attachment.file instanceof File) {
          formDataToSend.append('files', attachment.file);
        }
      }
      
      // Call an updated API method that supports FormData
      const response = await api.postFormData<Project>('projects', formDataToSend);
      await fetchProjects();
      navigate('/');
    } catch (error) {
      console.error('Error creating project:', error);
      // Add appropriate error handling feedback to the user
    } finally {
      setIsSubmitting(false);
    }
  };// Update the handleFileUpload function in ProjectForm.tsx
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setFileError(null);
  
    const newAttachments: Attachment[] = [];
    let hasError = false;
  
    files.forEach(file => {
      const validation = validateFile(file, {
        sizeExceeded: (size) => t('errors.file.sizeExceeded', { size }),
        typeNotSupported: t('errors.file.typeNotSupported')
      });
      
      if (!validation.isValid) {
        setFileError(validation.error);
        hasError = true;
        return;
      }
  
      newAttachments.push({
        id: Date.now() + newAttachments.length,
        name: file.name,
        type: getFileType(file.name),
        size: file.size,
        uploadedAt: new Date().toISOString(),
        file: file // Store the File object itself
      });
    });
  
    if (!hasError) {
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments]
      }));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto">
      <PreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        dxfContent={formData.dxfContent || ''}
      />
      <h2 className="text-xl font-bold mb-6">{t('projectForm.title')}</h2>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            {t('projectForm.fields.name.label')} *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className={`mt-1 block w-full rounded-md border ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            } px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{t('projectForm.fields.name.required')}</p>}
        </div>

        <div>
          <label htmlFor="client" className="block text-sm font-medium text-gray-700">
            {t('projectForm.fields.client.label')} *
          </label>
          <input
            type="text"
            id="client"
            value={formData.client}
            onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
            className={`mt-1 block w-full rounded-md border ${
              errors.client ? 'border-red-500' : 'border-gray-300'
            } px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
          />
          {errors.client && <p className="mt-1 text-sm text-red-500">{t('projectForm.fields.client.required')}</p>}
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700">
            {t('projectForm.fields.location.label')} *
          </label>
          <input
            type="text"
            id="location"
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            className={`mt-1 block w-full rounded-md border ${
              errors.location ? 'border-red-500' : 'border-gray-300'
            } px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
          />
          {errors.location && <p className="mt-1 text-sm text-red-500">{t('projectForm.fields.location.required')}</p>}
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            {t('projectForm.fields.description.label')} *
          </label>
          <textarea
            id="description"
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className={`mt-1 block w-full rounded-md border ${
              errors.description ? 'border-red-500' : 'border-gray-300'
            } px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
          />
          {errors.description && <p className="mt-1 text-sm text-red-500">{t('projectForm.fields.description.required')}</p>}
        </div>

        <div>
          <label htmlFor="dxfContent" className="block text-sm font-medium text-gray-700">
            {t('projectForm.fields.dxfContent.label')}
          </label>
          <div className="mt-1 relative">
            <textarea
              id="dxfContent"
              rows={5}
              value={formData.dxfContent || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, dxfContent: e.target.value }))}
              className="block w-full rounded-md border-gray-300 shadow-sm 
                focus:border-indigo-500 focus:ring-indigo-500"
              placeholder={t('projectForm.fields.dxfContent.placeholder')}
            />
            {formData.dxfContent && (
              <div className="mt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setPreviewModalOpen(true)}
                  className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {t('projectForm.buttons.preview')}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, dxfContent: '' }))}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  {t('projectForm.buttons.clear')}
                </button>
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('projectForm.fields.attachments.label')}
          </label>
          <div className="mt-1">
            <input
              type="file"
              multiple
              accept=".dwg,.pdf,.png,.jpg,.jpeg"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 
                file:rounded-md file:border-0 file:text-sm file:font-semibold 
                file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {fileError && (
              <p className="mt-1 text-sm text-red-500">{fileError}</p>
            )}
          </div>
          {formData.attachments.length > 0 && (
            <ul className="mt-2 divide-y divide-gray-200">
              {formData.attachments.map(file => (
                <li key={file.id} className="py-2 flex items-center justify-between">
                  <span className="text-sm text-gray-500">{file.name}</span>
                  <span className="text-xs text-gray-400">
                    ({file.type} - {formatFileSize(file.size)})
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => handleSubmit('DRAFT')}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 
              bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {t('projectForm.buttons.saveDraft')}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit('SUBMITTED')}
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white 
              bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {t('projectForm.buttons.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectForm;